import React, { useState, useEffect } from 'react'
import { Row, Col, Button, Container, ButtonGroup, Modal, Alert } from 'react-bootstrap'
import Table from 'react-bootstrap/Table';
import { useNavigate } from 'react-router-dom'
import { userAPI } from '../api/userAPI'
import ResizableTable from '../components/ResizableTable'
import { usePermissions } from '../contexts/PermissionContext'

function UsersListScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const [users, setUsers] = useState([])
    const [currentUser, setCurrentUser] = useState(null)
    const [sortOrder, setSortOrder] = useState('asc')
    const [showDeactivated, setShowDeactivated] = useState(false)
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [userToDeactivate, setUserToDeactivate] = useState(null);
    const navigate = useNavigate()

    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('userColumnWidths');
        return savedWidths ? JSON.parse(savedWidths) : {
            username: 'auto',
            first_name: 'auto',
            last_name: 'auto',
            email: 'auto',
            is_superuser: 'auto',
            actions: 330
        };
    });

    useEffect(() => {
        localStorage.setItem('userColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    useEffect(() => {
        async function fetchData() {
            const [allUsers, currentUserData] = await Promise.all([
                userAPI.getAll(),
                userAPI.getCurrentUser()
            ]);
            const sortedData = [...allUsers].sort((a, b) =>
                a.username.localeCompare(b.username)
            )
            setUsers(sortedData)
            setCurrentUser(currentUserData)
        }
        fetchData()
    }, [])

    const filteredUsers = users.filter(u => 
        showDeactivated ? true : u.is_active
    )

    const handleSort = () => {
        const sortedUsers = [...filteredUsers].sort((a, b) => {
            const comparison = a.username.localeCompare(b.username)
            return sortOrder === 'asc' ? comparison : -comparison
        })
        setUsers(sortedUsers)
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    }

    const handleToggleActive = (e, user) => {
        e.stopPropagation();
        
        const currentUsername = localStorage.getItem('username');
        
        if (user.is_active) {
            if (user.username === currentUsername) {
                setUserToDeactivate(user);
                setShowDeactivateModal(true);
                return;
            } else {
                if (!window.confirm('Are you sure you want to deactivate this user?')) {
                    return;
                }
            }
        }

        deactivateUser(user);
    };

    const deactivateUser = async (user) => {
        try {
            const updatedUser = {
                ...user,
                is_active: !user.is_active
            };
            
            await userAPI.update(user.id, updatedUser);
            
            setUsers(users.map(u => 
                u.id === user.id 
                    ? updatedUser
                    : u
            ));

            const currentUsername = localStorage.getItem('username');
            if (user.username === currentUsername && !updatedUser.is_active) {
                localStorage.clear();
                window.location.href = '/login';
            }
        } catch (error) {
            console.error('Error toggling user status:', error);
            alert(`Failed to update user: ${error.response?.data?.message || error.message}`);
        }
    };

    if (permissionsLoading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        )
    }

    if (!hasPermission('users.view')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to view users.
                </Alert>
            </Container>
        )
    }

    return (
        <Container className="py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Users</h1>
                </Col>
                <Col xs="auto">
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowDeactivated(!showDeactivated)}
                        className="me-2"
                    >
                        {showDeactivated ? 'Hide Deactivated' : 'Show Deactivated'}
                    </Button>
                    {hasPermission('users.modify') && (
                        <Button
                            onClick={() => navigate('/user/create')}
                            variant="success"
                        >
                            Create User
                        </Button>
                    )}
                </Col>
            </Row>

            <ResizableTable
                tableId="users_table"
                columns={[
                    {
                        id: 'username',
                        header: () => (
                            <div 
                                onClick={handleSort} 
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleSort();
                                    }
                                }}
                                style={{ cursor: 'pointer' }}
                                tabIndex={0}
                                role="button"
                                aria-label="Sort by username"
                            >
                                Username {sortOrder === 'asc' ? '↑' : '↓'}
                            </div>
                        ),
                        cell: (row) => (
                            <>
                                {row.username}
                                {!row.is_active && (
                                    <span className="ms-2 text-muted fst-italic">
                                        (Inactive)
                                    </span>
                                )}
                            </>
                        )
                    },
                    {
                        id: 'first_name',
                        header: 'First Name',
                        cell: (row) => row.first_name
                    },
                    {
                        id: 'last_name',
                        header: 'Last Name',
                        cell: (row) => row.last_name
                    },
                    {
                        id: 'email',
                        header: 'Email',
                        cell: (row) => row.email
                    },
                    {
                        id: '2fa_status',
                        header: '2FA Status',
                        cell: (row) => (
                            <span className={`badge ${row.two_factor_enabled ? 'bg-success' : 'bg-secondary'}`}>
                                {row.two_factor_enabled ? 'Enabled' : 'Disabled'}
                            </span>
                        )
                    },
                    {
                        id: 'is_superuser',
                        header: 'Type',
                        cell: (row) => (
                            <>
                                {row.is_superuser && (
                                    <span className="badge bg-success me-1">
                                        Superuser
                                    </span>
                                )}
                                {row.is_permission_admin && (
                                    <span className="badge bg-danger">
                                        Permission Admin
                                    </span>
                                )}
                            </>
                        )
                    },
                    {
                        id: 'actions',
                        header: 'Actions',
                        cell: (row) => (
                            <ButtonGroup size="sm" className="gap-1">
                                {hasPermission('users.modify') && (
                                    <Button
                                        variant={row.is_active ? "danger" : "success"}
                                        onClick={(e) => handleToggleActive(e, row)}
                                        size="sm"
                                    >
                                        {row.is_active ? 'Deactivate' : 'Activate'}
                                    </Button>
                                )}
                                {row.is_active && hasPermission('users.modify') && (
                                    <>
                                        <Button
                                            variant="primary"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/user/${row.id}/change-password`);
                                            }}
                                            size="sm"
                                        >
                                            Change Password
                                        </Button>
                                        {currentUser && (currentUser.is_superuser || currentUser.is_permission_admin) && (
                                            <Button
                                                variant="info"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/user/${row.id}/permissions`);
                                                }}
                                                size="sm"
                                            >
                                                Permissions
                                            </Button>
                                        )}
                                    </>
                                )}
                            </ButtonGroup>
                        )
                    }
                ]}
                data={filteredUsers}
                initialColumnWidths={columnWidths}
                onRowClick={(row) => navigate(`/user/${row.id}`)}
                striped
                hover
            />

            {/* Deactivation Confirmation Modal */}
            <Modal
                show={showDeactivateModal}
                onHide={() => setShowDeactivateModal(false)}
                centered
            >
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        Warning: Account Deactivation
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="alert alert-warning">
                        <h5>You are about to deactivate your own account!</h5>
                        <p>This action will have the following consequences:</p>
                        <ul className="mb-0">
                            <li>You will be immediately logged out</li>
                            <li>Your account will be locked</li>
                            <li>You will not be able to log in again</li>
                        </ul>
                    </div>
                    <p className="mb-0 text-danger">
                        Are you sure you want to proceed with this action?
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => setShowDeactivateModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={() => {
                            setShowDeactivateModal(false);
                            if (userToDeactivate) {
                                deactivateUser(userToDeactivate);
                                setUserToDeactivate(null);
                            }
                        }}
                    >
                        Yes, Deactivate My Account
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    )
}

export default UsersListScreen
